import React from 'react'
import {
  T, Avatar, RatingBadge, tierFromProb, confColor, probColor, bookName, fmtOdds,
  GradeRing, gradeColor, gradeLetter,
} from '../nfl/ui'

// Shared CFB card components — the board PickCard + the game card, plus the small
// helpers they share. Kept in one file so every surface (Home, Board, Games) renders
// a play identically, exactly the way the NFL app splits cards.jsx out of the app.

export const propLine = p => p.prop === 'anytime_td' ? '0.5'
  : (p.book_line != null ? p.book_line : p.line)

export function matchupRead(adj) {
  const v = adj != null ? adj : 1
  if (v >= 1.07) return { label: 'Great', color: T.green }
  if (v >= 1.02) return { label: 'Good', color: '#34D399' }
  if (v <= 0.93) return { label: 'Tough', color: T.red }
  if (v <= 0.98) return { label: 'Below avg', color: T.amber }
  return { label: 'Neutral', color: T.muted2 }
}

// Coarse bucket for the Board's matchup filter (folds "Below avg" into Tough).
export function matchupBucket(adj) {
  const v = adj != null ? adj : 1
  if (v >= 1.07) return 'Great'
  if (v >= 1.02) return 'Good'
  if (v <= 0.98) return 'Tough'
  return 'Neutral'
}

export function plainRead(tier, isTD) {
  if (tier === 'ELITE' || tier === 'BEST BET')
    return isTD ? 'Top scoring spot — heavy role in a strong game script.' : 'Model’s top tier — projects comfortably over the line.'
  if (tier === 'STRONG') return isTD ? 'Strong scoring lean — big opportunity share.' : 'Solid projected edge over the line.'
  if (tier === 'PLAY') return 'Moderate lean — projects over, but not a lock.'
  if (tier === 'WATCH') return 'Thin edge — watch before the number moves.'
  return 'Under the line on our projection — likely a pass.'
}

export function MiniStat({ label, children }) {
  return (
    <div style={{ background: T.card3, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 9px' }}>
      <div style={{ fontSize: 9, color: T.faint, letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      {children}
    </div>
  )
}

// Little recent-form bar chart (green = cleared the line, red = didn't) for the modal.
export function HitBars({ games }) {
  if (!games || !games.length) return <span style={{ fontSize: 11, color: T.faint }}>—</span>
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 22 }}>
      {games.map((g, i) => (
        <div key={i} title={`W${g.week}: ${g.val}`} style={{ width: 7, height: g.hit ? 22 : 12,
          borderRadius: 2, background: g.hit ? T.green : T.red, opacity: g.hit ? 1 : 0.6 }} />
      ))}
    </div>
  )
}

// The board card — mirrors the NFL PickCard.
export function PickCard({ p, rank, onTap }) {
  const isTD = p.prop === 'anytime_td'
  const hasBook = p.book_line != null && !isTD
  const lineIsModel = !hasBook && !isTD
  const lineLabel = isTD ? '0.5' : (hasBook ? p.book_line : p.line)
  const hit = Math.round((p.prob || 0) * 100)
  const tier = tierFromProb(p.prob, p.confidence)
  const edge = p.edge_pct
  const mr = matchupRead(p.def_adj)
  const kick = fmtKickShort(p)
  return (
    <button onClick={() => onTap && onTap(p)} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: onTap ? 'pointer' : 'default',
      color: T.text, background: T.card, border: `1px solid ${rank <= 3 ? T.greenMid : T.border}`,
      borderRadius: 12, padding: 14, marginBottom: 8 }}>
      {kick && (
        <div style={{ fontSize: 10.5, fontWeight: 700, color: T.faint, letterSpacing: '.02em', marginBottom: 7 }}>🕒 {kick}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <RatingBadge tier={tier} size={34} />
          <Avatar name={p.name} size={34} />
          <span style={{ fontSize: 16, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.muted }}>{p.pos}</span>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 19, lineHeight: 1, color: probColor(p.prob) }}>{hit}%</div>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: '.04em' }}>{isTD ? 'TO SCORE' : 'TO HIT'}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11.5, color: T.muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {/* Unpriced plays have no real book line yet — show the market (projection is in
              the tile below), not a flat internal line, so "O59.5 @ 73%" can't mislead. */}
          {p.team} · vs {p.opp} · <b style={{ color: T.text }}>{isTD ? 'Anytime TD' : (hasBook ? `${p.market} O${p.book_line}` : p.market)}</b>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {p.line_suspect && (
            <span style={{ fontSize: 11, fontWeight: 800, color: T.amber, background: T.amberDim, border: `1px solid ${T.amber}`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>⚠ Line looks off</span>
          )}
          {!p.line_suspect && edge != null && Math.abs(edge) >= 0.5 && (
            <span style={{ fontSize: 11, fontWeight: 800, color: edge >= 0 ? T.green : T.red, background: edge >= 0 ? T.greenDim : 'transparent', border: `1px solid ${edge >= 0 ? T.green : T.red}`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>{edge >= 0 ? '+' : ''}{edge.toFixed(1)}% edge</span>
          )}
          {!p.line_suspect && p.value && (
            <span style={{ fontSize: 11, fontWeight: 800, color: '#04140D', background: T.green, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>✓ Value</span>
          )}
        </div>
      </div>
      {/* The generic tier sentence lived here and repeated identically on every card —
          pure noise. It now lives only in the player modal. The scan row below carries
          the real per-play signal. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 8 }}>
        <MiniStat label="Matchup"><span style={{ fontSize: 13, fontWeight: 800, color: mr.color }}>{mr.label}</span></MiniStat>
        <MiniStat label="Projection"><span style={{ fontSize: 13, fontWeight: 800, fontFamily: T.mono, color: T.text }}>{p.proj != null ? p.proj : '—'}</span></MiniStat>
        <MiniStat label="Confidence"><span style={{ fontSize: 13, fontWeight: 800, fontFamily: T.mono, color: confColor(p.confidence) }}>{p.confidence || '—'}</span></MiniStat>
      </div>
      {hasBook && !p.line_suspect ? (
        <div style={{ fontSize: 11, color: T.muted2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ color: T.green, fontWeight: 700 }}>💰 Best {fmtOdds(p.book_over_odds)}</span>
          <span>{bookName(p.book_used)} · O{p.book_line}</span>
        </div>
      ) : lineIsModel ? (
        <div style={{ fontSize: 11, color: T.faint }}>Model estimate for this player · book line not posted yet</div>
      ) : null}
      {/* Calibration caution from the backtest (TDs run hot, thin markets, etc.) */}
      {p.caution && (
        <div style={{ fontSize: 10.5, color: T.amber, marginTop: 6, lineHeight: 1.35 }}>⚠ {p.caution}</div>
      )}
    </button>
  )
}

// Compact one-line play row (for game cards + lists).
export function PlayRow({ p, onTap, border }) {
  const hit = Math.round((p.prob || 0) * 100)
  const tier = tierFromProb(p.prob, p.confidence)
  return (
    <button onClick={() => onTap && onTap(p)} style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 9,
      padding: '8px 0', borderTop: border ? `1px solid ${T.border}` : 'none', background: 'none', border: 'none', cursor: 'pointer' }}>
      <RatingBadge tier={tier} size={26} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.name} <span style={{ fontSize: 10.5, color: T.muted, fontWeight: 600 }}>{p.pos}</span>
        </div>
        <div style={{ fontSize: 10.5, color: T.muted2 }}>
          {p.prop === 'anytime_td' ? 'Anytime TD'
            : (p.book_line != null ? `${p.market} O${p.book_line}` : p.market)}
          {p.value ? <span style={{ color: T.green, fontWeight: 700 }}> · ✓ Value</span> : null}
          {p.caution ? <span title={p.caution} style={{ color: T.amber }}> · ⚠</span> : null}
        </div>
      </div>
      <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 14, color: probColor(p.prob), flexShrink: 0 }}>{hit}%</span>
    </button>
  )
}

function fmtKick(g) {
  try {
    if (g.gametime) {
      const d = new Date(g.gametime)
      if (!isNaN(d)) return d.toLocaleString(undefined,
        { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    }
  } catch (_) {}
  return g.gameday || ''
}

// Compact kickoff for a board card: weekday + local time (e.g. "Sat 3:30 PM").
function fmtKickShort(g) {
  try {
    if (g.gametime) {
      const d = new Date(g.gametime)
      if (!isNaN(d)) return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    }
  } catch (_) {}
  return g.day || g.gameday || ''
}

// Game card — mirrors the NFL GameCard: kickoff, matchup + grade ring, projected TDs,
// an intel pill row, the Vegas line, then the game's top plays.
export function GameCard({ g, onTap }) {
  const spread = g.spread_line
  const favTxt = spread == null ? null
    : spread < 0 ? `${g.home} ${spread}` : spread > 0 ? `${g.away} -${spread}` : 'Pick’em'
  const kickoff = fmtKick(g)
  const grade = g.grade || 0
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 15px 8px', marginBottom: 10 }}>
      {kickoff && (
        <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 8 }}>🕒 {kickoff}</div>
      )}
      {/* Matchup + grade ring */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15.5, fontWeight: 900, color: T.text, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
            {g.away}
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 900, color: T.text, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
            <span style={{ color: T.faint, fontWeight: 600, fontSize: 12 }}>@ </span>{g.home}
          </div>
          {(g.away_conference || g.home_conference) && (
            <div style={{ fontSize: 10, color: T.faint, marginTop: 3 }}>
              {[g.away_conference, g.home_conference].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        {grade > 0 && <GradeRing grade={grade} />}
      </div>
      {/* Projected TDs */}
      {g.proj_tds != null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 11,
          background: T.card2, border: `1px solid ${T.border2}`, borderRadius: 9, padding: '8px 11px' }}>
          <span style={{ fontSize: 11.5, color: T.muted }}>🏈 Projected TDs{g.td_leader ? <> · led by <b style={{ color: T.text }}>{g.td_leader}</b></> : null}</span>
          <span style={{ fontSize: 20, fontWeight: 900, fontFamily: T.mono, color: T.green, letterSpacing: '-0.02em' }}>{g.proj_tds.toFixed(1)}</span>
        </div>
      )}
      {/* Intel pills */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 10 }}>
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: gradeColor(grade), background: T.greenDim, border: `1px solid ${T.border2}`, borderRadius: 7, padding: '5px 8px', textAlign: 'center' }}>Action {grade ? gradeLetter(grade) : '—'}</span>
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: T.muted2, background: T.card2, border: `1px solid ${T.border2}`, borderRadius: 7, padding: '5px 8px', textAlign: 'center' }}>{g.total_line != null ? `O/U ${g.total_line}` : 'O/U —'}</span>
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: T.muted2, background: T.card2, border: `1px solid ${T.border2}`, borderRadius: 7, padding: '5px 8px', textAlign: 'center' }}>{g.n_plays ?? 0} plays</span>
      </div>
      {/* Line */}
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>
        {favTxt ? <>Spread <b style={{ color: T.text }}>{favTxt}</b></> : <span style={{ color: T.faint }}>Line not posted</span>}
      </div>
      {(g.plays || []).map((p, i) => <PlayRow key={i} p={p} onTap={onTap} border={i > 0} />)}
    </div>
  )
}
